import { pick } from 'lodash-es'

import { downloadFileByLink } from '@/lib/downloadFileByLink'
import { replaceEntity } from '@/lib/entity/replaceEntity'
import { captureAndLogError } from '@/lib/error'
import { IAction } from '@/redux/actions/action-helpers'
import { ExpenseReportsTypes } from '@/redux/actions/actions.types'
import { ApplicationState } from '@/redux/stores/types'
import { ExpenseReportHandler } from '@/travelsuit/expenses'
import {
	ExpenseReportModel,
	ExpenseReportModelZ,
	ExpenseReportRequest,
	ExpenseReportRequestZ,
	ExpenseReportResponse,
} from '@/types/expenses/report'

import { normalize as normalizeExpense } from './expenses.reducer'

function expenseReportsReducer(state: ApplicationState['expenseReports'] = [], action: IAction) {
	switch (action.type) {
		case ExpenseReportsTypes.GetReports.SUCCESS:
			return action.payload.map(normalize)
		case ExpenseReportsTypes.GetReport.SUCCESS:
		case ExpenseReportsTypes.SubmitReport.SUCCESS:
		case ExpenseReportsTypes.ApproveReport.SUCCESS:
		case ExpenseReportsTypes.RejectReport.SUCCESS: {
			const normalized = normalize(action.payload)
			return replaceEntity(state, normalized.data.id, normalized, (entity) => entity.data.id)
		}
		case ExpenseReportsTypes.ExportReports.SUCCESS: {
			downloadFileByLink(
				window.URL.createObjectURL(action.payload),
				`expense-reports-${new Date().toISOString().slice(0, 10)}.${action.requestPayload.fileType}`,
			)

			return state
		}
		default:
			return state
	}
}

export default expenseReportsReducer

export function normalize(report: ExpenseReportResponse): ExpenseReportHandler {
	const parsedReport = ExpenseReportModelZ.safeParse({
		id: report.id,
		name: report.name,
		status: report.status,
		dates: report.start_date && report.end_date ? [new Date(report.start_date), new Date(report.end_date)] : null,
		description: report.description,
		submitter: report.submitter,
		approver: report.approver,
		expenses: report.expenses.map(normalizeExpense),
		reasons:
			report.reasons?.map((reason) => ({
				reason: reason.reason,
				rejecter: reason.rejecter,
				id: reason.id,
				createdAt: new Date(reason.created_dt),
			})) ?? [],
		createdAt: new Date(report.created_dt),
		modifiedAt: report.last_modified_dt ? new Date(report.last_modified_dt) : null,
		modifiedStatusAt: report.last_status_modified_dt ? new Date(report.last_status_modified_dt) : null,
	})

	if (!parsedReport.success) {
		captureAndLogError(parsedReport.error, { level: 'error', extra: { source: 'response_validation' } })
		console.error(report)
	}

	return new ExpenseReportHandler(parsedReport.data!)
}

export function denormalize(expenseReport: ExpenseReportModel): ExpenseReportRequest {
	const { dates } = expenseReport

	const parsedReport = ExpenseReportRequestZ.safeParse({
		...pick(expenseReport, ['name', 'description']),
		expenses: expenseReport.expenses.map((expense) => expense.data.id),
		start_date: dates?.[0] ? new Date(dates[0].toString().slice(0, 28)).toISOString().slice(0, 10) : undefined,
		end_date: dates?.[1] ? new Date(dates[1].toString().slice(0, 28)).toISOString().slice(0, 10) : undefined,
	})

	if (!parsedReport.success) {
		captureAndLogError(parsedReport.error, { level: 'error', extra: { source: 'request_validation' } })
		console.error(expenseReport)
	}

	return parsedReport.data!
}
